import { useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { TextField, Radio, NumbersOnly, FieldData } from 'vopay';

import { PaymentFieldDefault } from './types';
import { useClientData } from '@/providers/ClientData.provider';

export function useContactDetailsFields(): FieldData<PaymentFieldDefault>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  const {
    response: { Country },
  } = useClientData();
  return useMemo(() => {
    return [
      {
        label: () => t('Fields.contactType.label'),
        fieldName: 'ContactType',
        getInputState: () => 'required',
        showField: () => true,
        renderField: ({ ctx, getFormikData }) => {
          return (
            <Radio
              {...getFormikData<string>({
                onChange: (e) => {
                  ctx.setValues({
                    ...ctx.values,
                    ContactType: e.target.value,
                  });
                  ctx.setTouched({
                    ...ctx.touched,
                    FirstName: false,
                    LastName: false,
                    CompanyName: false,
                    ContactType: true,
                  });
                },
              })}
              variant="outlineShadow"
              noOrdering
              options={{
                individual: t('contactType.individual'),
                business: t('contactType.business'),
              }}
            />
          );
        },
      },
      {
        label: () => t('Fields.firstName.label'),
        fieldName: 'FirstName',
        getInputState: () => 'required',
        showField: (values) => values.Contact === 'new' && values.ContactType === 'individual',
        serializeField: (values) => {
          return values?.FirstName?.replace(/, /g, ' ').replace(/,/g, '') || '';
        },
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" placeholder="John" {...fieldData} />;
        },
        validate: () => Yup.mixed().required(),
      },
      {
        label: () => t('Fields.lastName.label'),
        fieldName: 'LastName',
        getInputState: () => 'required',
        showField: (values) => values.Contact === 'new' && values.ContactType === 'individual',
        serializeField: (values) => {
          return values?.LastName?.replace(/, /g, ' ').replace(/,/g, '') || '';
        },
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" placeholder="Doe" {...fieldData} />;
        },
        validate: () => Yup.mixed().required(),
      },
      {
        label: () => t('Fields.companyName.label'),
        fieldName: 'CompanyName',
        getInputState: () => 'required',
        showField: (values) => values.Contact === 'new' && values.ContactType === 'business',
        serializeField: (values) => {
          return values?.CompanyName?.replace(/, /g, ' ').replace(/,/g, '') || '';
        },
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" {...fieldData} />;
        },
        validate: () => Yup.mixed().required(),
      },
      {
        label: () => t('Fields.emailAddress.label'),
        fieldName: 'NewEmailAddress',
        getInputState: () => 'optional',
        showField: (values) => values.Contact === 'new',
        serializeField: (values) => values.NewEmailAddress,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField variant="outlineShadow" preset="email" placeholder="example@email.com" {...fieldData} />;
        },
      },
      {
        label: () => t('Fields.recipientPhoneNumber.label'),
        fieldName: 'NewPhoneNumber',
        getInputState: (ctx) =>
          ctx.values.PaymentSelectionMethod === 'bank' && Country === 'US' ? 'required' : 'optional',
        showField: (values) => values.Contact === 'new',
        serializeField: (values) => NumbersOnly(values.NewPhoneNumber || ''),
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField {...fieldData} variant="outlineShadow" preset="tel" />;
        },
        validate: (ctx) =>
          ctx.values.PaymentSelectionMethod === 'bank' && Country === 'US'
            ? Yup.string().required()
            : Yup.string().notRequired(),
      },
    ];
  }, []);
}
